import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ChangeEvent, FC, ReactElement } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import DotsLoader from 'components/DotsLoader/DotsLoader';
import { useUserProfile } from 'contexts/UserProfileContext';
import { WipPriorityE, WipSortFieldE, WipSortOrderE } from 'declarations/enums/wip';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { WipRecordI, WipRecordListI } from 'declarations/interfaces/wip';
import { getErrorMessage } from 'helpers/apiError';
import {
  useAddWipRecord,
  useFetchWipDealerSummary,
  useFetchWipOemFamilyClaimStatuses,
} from 'hooks/api/useWip';
import TicketsListViewLoader from 'pages/TicketsPage/components/TicketsListView/components/TicketsListViewLoader';
import { ListViewStyled } from 'pages/TicketsPage/components/TicketsListView/TicketsListView.styled';
import {
  SortableColumnLabelStyled,
  SortableColumnTextStyled,
} from 'pages/TicketsPage/components/TicketsSorting/TicketsSorting.styled';
import { MessageWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import {
  selectWipColumnOrder,
  selectWipSelectedIds,
  selectWipSelectedRoofTopId,
  selectWipSorting,
  setWipSelectedIds,
  setWipSorting,
  toggleWipSelectedId,
} from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';

import type { WipEditableRowT } from './validateWipRow';
import type { WipDataEntryDealerI } from './WIPItemRow';
import { WIPItemRow } from './WIPItemRow';
import type { WipItemCellI } from './WIPItemsTableConfiguration';
import { resolveVisibleWipColumns } from './WIPItemsTableConfiguration';

const COLUMN_LABEL_TO_SORT_FIELD: Record<string, WipSortFieldE> = {
  'RO Number': WipSortFieldE.RepairOrderNumber,
  Dealership: WipSortFieldE.Dealership,
  OEM: WipSortFieldE.Oem,
  Age: WipSortFieldE.CreatedAt,
  'Advisor Number': WipSortFieldE.AdvisorNumber,
  'Tech Number': WipSortFieldE.TechNumber,
  Priority: WipSortFieldE.Priority,
  'Submitted Date': WipSortFieldE.SubmittedDate,
  'OEM Paid Date': WipSortFieldE.OemPaidDate,
};

const formatMoney = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

const renderCellValue = (column: WipItemCellI, record: WipRecordI): string => {
  const raw = record[column.id];

  if (column.isMoneyAmount) {
    return formatMoney(raw as number | null);
  }
  if (
    column.id === 'openRepairOrderDate' ||
    column.id === 'oemPaidDate' ||
    column.id === 'submittedDate'
  ) {
    return formatDate(raw as string | null);
  }
  if (column.id === 'backlog' || column.id === 'isSublet') {
    return raw ? 'Yes' : 'No';
  }
  if (raw === null || raw === undefined || raw === '') return '-';
  return String(raw);
};

function TruncatedWithTooltip({
  value,
  color = 'text.primary',
}: {
  value: string | null | undefined;
  color?: string;
}): ReactElement {
  const display = value != null && value !== '' ? value : '-';
  return (
    <Tooltip title={display} enterDelay={400} arrow placement="top-start">
      <Typography
        variant="body2"
        color={color}
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  );
}

type DataRowPropsT = {
  record: WipRecordI;
  visibleColumns: WipItemCellI[];
  isItemSelected: boolean;
  onToggleSelected: (wipId: string) => void;
  checkboxColumnWidth: number;
};

const CHECKBOX_SIZE = 42;
const CHECKBOX_ICON_FONT_SIZE = 24;

function DataRow({
  record,
  visibleColumns,
  isItemSelected,
  onToggleSelected,
  checkboxColumnWidth,
}: DataRowPropsT): ReactElement {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const wipId = record.wipId;

  const handleCheckboxClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (!wipId) return;
    event.stopPropagation();
    onToggleSelected(wipId);
  };

  return (
    <TableRow
      hover
      selected={isItemSelected}
      onMouseEnter={(): void => setIsHovered(true)}
      onMouseLeave={(): void => setIsHovered(false)}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell
        padding="checkbox"
        sx={{
          width: checkboxColumnWidth,
          minWidth: checkboxColumnWidth,
          backgroundColor: 'transparent',
        }}
      >
        {wipId != null && (
          <Box display="flex" justifyContent="flex-start" alignItems="center" height="100%">
            <Checkbox
              size="medium"
              color="primary"
              checked={isItemSelected}
              onClick={handleCheckboxClick}
              sx={{
                width: CHECKBOX_SIZE,
                height: CHECKBOX_SIZE,
                '& .MuiSvgIcon-root': { fontSize: CHECKBOX_ICON_FONT_SIZE },
                display: isHovered || isItemSelected ? 'block' : 'none',
              }}
              inputProps={{ 'aria-label': `Select WIP item ${record.repairOrderNumber}` }}
            />
          </Box>
        )}
      </TableCell>
      {visibleColumns.map((column): JSX.Element => {
        const content = renderCellValue(column, record);
        return (
          <TableCell
            key={column.id}
            align={column.alignment}
            sx={{
              minWidth: column.minWidth,
              width: column.width ?? column.minWidth,
            }}
          >
            <TruncatedWithTooltip value={content} />
          </TableCell>
        );
      })}
    </TableRow>
  );
}

export type WIPItemsTablePropsT = {
  wipPages: WipRecordListI[] | undefined;
  totalCount: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  visibleColumns: WipItemCellI[];
};

const WIPItemsTable: FC<WIPItemsTablePropsT> = ({
  wipPages,
  totalCount,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  visibleColumns: defaultVisibleColumns,
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const sorting = useAppSelector(selectWipSorting);
  const columnOrder = useAppSelector(selectWipColumnOrder);
  const selectedWipIds = useAppSelector(selectWipSelectedIds);
  const selectedDealerCardRoofTopId = useAppSelector(selectWipSelectedRoofTopId);
  const selectedWipIdsSet = useMemo((): Set<string> => new Set(selectedWipIds), [selectedWipIds]);
  const { userProfile } = useUserProfile();

  const { data: dealerSummary } = useFetchWipDealerSummary();
  const { data: oemFamilyClaimStatuses } = useFetchWipOemFamilyClaimStatuses();
  const addWipRecordMutation = useAddWipRecord();

  // Bumping this key resets the new-row inputs after a successful save.
  const [newRowKey, setNewRowKey] = useState<number>(0);

  // Dealer dropdown for the data entry row: cross-section of the user's mapped rooftops and
  // the rooftops surfaced in the WIP dealer card (which is already filtered to the WIP-enabled
  // set on the backend), enriched with the OEM family ID so the row can resolve the matching
  // claim status catalog when a dealer is selected.
  const dataEntryDealers: WipDataEntryDealerI[] = useMemo((): WipDataEntryDealerI[] => {
    const mapped = userProfile?.mappedRoofTops ?? [];
    const summaryByRoofTopId = new Map<string, { oemFamilyId: string; roofTopName: string }>(
      (dealerSummary?.dealers ?? []).map(
        (d): [string, { oemFamilyId: string; roofTopName: string }] => [
          String(d.roofTopId),
          { oemFamilyId: String(d.oemFamilyId ?? ''), roofTopName: d.roofTopName },
        ]
      )
    );
    if (summaryByRoofTopId.size === 0) {
      return mapped.map((r): WipDataEntryDealerI => ({ id: r.id, name: r.name, oemFamilyId: '' }));
    }
    return mapped
      .filter((r): boolean => summaryByRoofTopId.has(r.id))
      .map((r): WipDataEntryDealerI => {
        const summary = summaryByRoofTopId.get(r.id);
        return {
          id: r.id,
          name: r.name || summary?.roofTopName || r.id,
          oemFamilyId: summary?.oemFamilyId ?? '',
        };
      });
  }, [userProfile?.mappedRoofTops, dealerSummary?.dealers]);

  /**
   * Resolve the configured column order + visibility into the concrete `WipItemCellI[]` the
   * table renders. When the picker has not been touched yet (columnOrder is empty), we use the
   * caller-provided default visible columns to preserve existing behavior on first paint.
   */
  const visibleColumns: WipItemCellI[] = useMemo((): WipItemCellI[] => {
    if (columnOrder.length === 0) return defaultVisibleColumns;
    return resolveVisibleWipColumns(columnOrder);
  }, [columnOrder, defaultVisibleColumns]);

  // The new-row save fires from the last editable column on Tab; pick the last visible
  // column that isn't read-only metadata.
  const lastEditableColumn = useMemo(
    (): WipItemCellI | undefined =>
      [...visibleColumns]
        .reverse()
        .find(
          (c): boolean =>
            c.id !== 'hasTickets' &&
            c.id !== 'oemFamilyName' &&
            c.id !== 'age' &&
            c.id !== 'wipStatus'
        ),
    [visibleColumns]
  );

  const handleValidationError = useCallback(
    (message: string): void => {
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: message,
          snackBarSeverity: 'error',
        })
      );
    },
    [dispatch]
  );

  const handleNewRowSave = useCallback(
    (payload: WipEditableRowT): void => {
      addWipRecordMutation.mutate(
        {
          repairOrderNumber: payload.repairOrderNumber ?? '',
          roofTopId: payload.roofTopId ?? '',
          openRepairOrderDate: payload.openRepairOrderDate ?? '',
          lineNumber: payload.lineNumber ?? '',
          advisorNumber: payload.advisorNumber ?? '',
          techNumber: payload.techNumber ?? '',
          oemClaimStatus: payload.oemClaimStatus ?? '',
          assigneeId: payload.assigneeId ?? null,
          priority: payload.priority ?? WipPriorityE.Undefined,
          backlog: payload.backlog ?? false,
          isSublet: payload.isSublet ?? false,
          repairOrderTotal: payload.repairOrderTotal ?? null,
          partsAmountClaimed: payload.partsAmountClaimed ?? null,
          partsAmountPaid: payload.partsAmountPaid ?? null,
          laborAmountClaimed: payload.laborAmountClaimed ?? null,
          laborAmountPaid: payload.laborAmountPaid ?? null,
          techTime: payload.techTime ?? null,
          oemPaidTotal: payload.oemPaidTotal ?? null,
          oemPaidDate: payload.oemPaidDate ?? null,
          submittedDate: payload.submittedDate ?? null,
          repairOrderStatusEnum: payload.repairOrderStatusEnum ?? null,
          notes: payload.notes ?? null,
        },
        {
          onSuccess: (): void => {
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: 'WIP item successfully added',
                snackBarSeverity: 'success',
              })
            );
            setNewRowKey((k): number => k + 1);
          },
          onError: (error: ApiErrorI): void => {
            const errorMsg = getErrorMessage(error) ?? '';
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: `Unable to add WIP item. ${errorMsg}`,
                snackBarSeverity: 'error',
              })
            );
          },
        }
      );
    },
    [addWipRecordMutation, dispatch]
  );

  const tableWrapRef = useRef<HTMLDivElement>(null);
  const { ref: lastRowObserver, inView } = useInView();

  useEffect((): void => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inView, isFetchingNextPage]);

  const handleSortClick = useCallback(
    (column: WipItemCellI) => (): void => {
      if (!column.sortable) return;
      const parameter = COLUMN_LABEL_TO_SORT_FIELD[column.label];
      if (!parameter) return;
      const nextOrder =
        sorting.parameter === parameter && sorting.order === WipSortOrderE.Asc
          ? WipSortOrderE.Desc
          : WipSortOrderE.Asc;
      dispatch(setWipSorting({ parameter, order: nextOrder }));
    },
    [dispatch, sorting]
  );

  const flattenedRecords: WipRecordI[] = useMemo(
    (): WipRecordI[] => wipPages?.flatMap((p): WipRecordI[] => p.data ?? []) ?? [],
    [wipPages]
  );

  const selectableWipIds = useMemo(
    (): string[] =>
      flattenedRecords
        .map((r): string | undefined => r.wipId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    [flattenedRecords]
  );

  const handleToggleRowSelected = useCallback(
    (wipId: string): void => {
      dispatch(toggleWipSelectedId(wipId));
    },
    [dispatch]
  );

  const handleSelectAllClick = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        dispatch(setWipSelectedIds(selectableWipIds));
      } else {
        dispatch(setWipSelectedIds([]));
      }
    },
    [dispatch, selectableWipIds]
  );

  // Drop any cached selection IDs that no longer correspond to a visible record (e.g. after a
  // filter change or page reload). Mirrors WarrQueue's resetSelection guard.
  useEffect((): void => {
    if (selectedWipIds.length === 0) return;
    const known = new Set(selectableWipIds);
    const filtered = selectedWipIds.filter((id): boolean => known.has(id));
    if (filtered.length !== selectedWipIds.length) {
      dispatch(setWipSelectedIds(filtered));
    }
  }, [dispatch, selectableWipIds, selectedWipIds]);

  const numSelected = selectedWipIds.length;
  const numSelectable = selectableWipIds.length;

  const showInitialLoader = isLoading && !isFetchingNextPage;
  const showLoadMoreIndicator = isFetchingNextPage && hasNextPage;

  /**
   * Default dealer dropdown for the data entry row (user request):
   *  - if the WIP dealer card has a single dealer available, pre-select it,
   *  - otherwise, if the user has explicitly picked a dealer in the dealer card, pre-select it,
   *  - otherwise, leave the dropdown unselected.
   *
   * The resolved value flows through the row's React key so picking a new dealer in the dealer
   * card transparently re-seeds the entry row with the new default (any in-progress edits in
   * the fresh row are discarded \u2014 the operator is changing context).
   */
  const defaultNewRowRoofTopId = useMemo<string | null>((): string | null => {
    if (dataEntryDealers.length === 1) return dataEntryDealers[0].id;
    if (
      selectedDealerCardRoofTopId &&
      dataEntryDealers.some((d): boolean => d.id === selectedDealerCardRoofTopId)
    ) {
      return selectedDealerCardRoofTopId;
    }
    return null;
  }, [dataEntryDealers, selectedDealerCardRoofTopId]);

  const newRow = (
    <WIPItemRow
      key={`new-row-${newRowKey}-${defaultNewRowRoofTopId ?? 'none'}`}
      isNewRow
      visibleColumns={visibleColumns}
      lastEditableColumn={lastEditableColumn}
      dealers={dataEntryDealers}
      oemFamilyClaimStatuses={oemFamilyClaimStatuses ?? []}
      onSave={handleNewRowSave}
      onValidationError={handleValidationError}
      defaultRoofTopId={defaultNewRowRoofTopId}
    />
  );

  const checkboxColumnWidth = 45;
  const isHeaderIndeterminate = numSelected > 0 && numSelected < numSelectable;
  const isHeaderChecked = numSelectable > 0 && numSelected === numSelectable;

  const renderHeaderCheckboxCell = (): JSX.Element => (
    <TableCell
      padding="checkbox"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        bgcolor: 'background.default',
        boxShadow: (theme): string => `inset 0 -1px 0 ${theme.palette.divider}`,
        width: checkboxColumnWidth,
        minWidth: checkboxColumnWidth,
      }}
    >
      <Checkbox
        color="primary"
        indeterminate={isHeaderIndeterminate}
        checked={isHeaderChecked}
        onChange={handleSelectAllClick}
        disabled={numSelectable === 0}
        inputProps={{ 'aria-label': 'Select all WIP items' }}
      />
    </TableCell>
  );

  if (!isLoading && totalCount === 0) {
    return (
      <ListViewStyled>
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': {
              px: 1,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <colgroup>
            <col style={{ width: checkboxColumnWidth }} />
            {visibleColumns.map(
              (col): JSX.Element => (
                <col key={col.id} style={{ width: col.width ?? col.minWidth }} />
              )
            )}
          </colgroup>
          <TableHead>
            <TableRow>
              {renderHeaderCheckboxCell()}
              {visibleColumns.map(
                (column): JSX.Element => (
                  <TableCell
                    key={column.id}
                    align={column.alignment}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      bgcolor: 'background.default',
                      boxShadow: (theme): string => `inset 0 -1px 0 ${theme.palette.divider}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {column.label}
                    </Typography>
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>{newRow}</TableBody>
        </Table>
        <MessageWrapStyled>
          <Typography variant="h6" color="text.primary" align="center">
            No WIP records found
          </Typography>
        </MessageWrapStyled>
      </ListViewStyled>
    );
  }

  return (
    <ListViewStyled ref={tableWrapRef}>
      <Table
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': {
            px: 1,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <colgroup>
          <col style={{ width: checkboxColumnWidth }} />
          {visibleColumns.map(
            (col): JSX.Element => (
              <col key={col.id} style={{ width: col.width ?? col.minWidth }} />
            )
          )}
        </colgroup>
        <TableHead>
          <TableRow>
            {renderHeaderCheckboxCell()}
            {visibleColumns.map((column): JSX.Element => {
              const sortField = COLUMN_LABEL_TO_SORT_FIELD[column.label];
              const isSortable = !!sortField && !!column.sortable;
              const isActive = isSortable && sorting.parameter === sortField;
              const direction: 'asc' | 'desc' =
                sorting.order === WipSortOrderE.Asc ? 'asc' : 'desc';
              return (
                <TableCell
                  key={column.id}
                  align={column.alignment}
                  sortDirection={isActive ? direction : false}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    bgcolor: 'background.default',
                    boxShadow: (theme): string => `inset 0 -1px 0 ${theme.palette.divider}`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    minWidth: column.minWidth,
                    width: column.width ?? column.minWidth,
                  }}
                >
                  {isSortable ? (
                    <SortableColumnLabelStyled
                      active={isActive}
                      direction={isActive ? direction : 'asc'}
                      onClick={handleSortClick(column)}
                    >
                      <SortableColumnTextStyled variant="body2" noWrap>
                        {column.label}
                      </SortableColumnTextStyled>
                    </SortableColumnLabelStyled>
                  ) : (
                    <Typography variant="body2" noWrap>
                      {column.label}
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        {showInitialLoader ? (
          <TableBody>
            {newRow}
            <TableRow>
              <TableCell colSpan={visibleColumns.length + 1} sx={{ border: 'none', p: 0 }}>
                <TicketsListViewLoader />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {newRow}
            {wipPages?.map(
              (page, pageIndex): JSX.Element => (
                <Fragment key={`page-${pageIndex}`}>
                  {page.data?.map((record): JSX.Element => {
                    const key = `${record.wipId ?? ''}-${record.repairOrderId}-${
                      record.lineNumber ?? ''
                    }`;
                    const wipId = record.wipId;
                    const isItemSelected = wipId != null && selectedWipIdsSet.has(wipId);
                    return (
                      <DataRow
                        key={key}
                        record={record}
                        isItemSelected={isItemSelected}
                        onToggleSelected={handleToggleRowSelected}
                        visibleColumns={visibleColumns}
                        checkboxColumnWidth={checkboxColumnWidth}
                      />
                    );
                  })}
                </Fragment>
              )
            )}
          </TableBody>
        )}
      </Table>
      <Box
        component="span"
        ref={lastRowObserver}
        sx={{ display: 'block', width: '100%', height: 20 }}
      />
      {showLoadMoreIndicator && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 1 }}>
          <DotsLoader />
        </Box>
      )}
      {!hasNextPage && flattenedRecords.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ width: '100%', py: 1 }}
        >
          {flattenedRecords.length.toLocaleString()} of {totalCount.toLocaleString()} record
          {totalCount === 1 ? '' : 's'}
        </Typography>
      )}
    </ListViewStyled>
  );
};

export default WIPItemsTable;
